/**
 * Files App Types
 *
 * Hierarchical file storage with S3 backend.
 * 8-char alphanumeric IDs for voice-friendly identification.
 */

// =============================================================================
// Core Types
// =============================================================================

export interface FileNode {
  id: string;  // 8-char alphanumeric
  user_id: number;
  parent_id: string | null;
  name: string;
  is_folder: boolean;
  file_size: number | null;
  content_type: string | null;
  description: string | null;
  tags: string[];
  path: string | null;  // Computed full path
  created_at: string;
  updated_at: string;
  folder_type: FolderType | null;  // System folder types (for protection)
  relevance?: number;   // Search relevance score (0-1) from hybrid search
  headline?: string;    // Search snippet with highlighted matches
}

// System folder types - protected folders cannot be deleted
export type FolderType = 'standard' | 'system' | 'task_root' | 'task' | 'iteration' | 'iteration_in' | 'iteration_out' | 'knowledge_root' | 'knowledge';

// Check if a folder is a protected system folder (cannot be deleted)
export function isSystemFolder(node: FileNode): boolean {
  return node.is_folder && node.folder_type !== null && node.folder_type !== 'standard';
}

export interface TreeNode {
  node: FileNode;
  children: TreeNode[];
}

export interface BreadcrumbItem {
  id: string | null;  // null for root
  name: string;
}

// =============================================================================
// View Types
// =============================================================================

export type FilesViewMode = 'grid' | 'list' | 'tree';
export type FilesSortBy = 'name' | 'size' | 'type' | 'modified';
export type FilesSortOrder = 'asc' | 'desc';

export interface FilesViewState {
  viewMode: FilesViewMode;
  sortBy: FilesSortBy;
  sortOrder: FilesSortOrder;
  showHiddenFiles: boolean;
}

// =============================================================================
// File Type Configuration
// =============================================================================

export interface FileTypeConfig {
  icon: string;
  color: string;
  bgColor: string;
  label: string;
}

export const FILE_TYPE_CONFIG: Record<string, FileTypeConfig> = {
  // Folders
  folder: {
    icon: '📁',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    label: 'Folder',
  },

  // System Folder Types (protected)
  folder_task_root: {
    icon: '📋',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    label: 'Tasks',
  },
  folder_task: {
    icon: '📌',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    label: 'Task',
  },
  folder_iteration: {
    icon: '🔄',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    label: 'Iteration',
  },
  folder_iteration_in: {
    icon: '📥',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'Input',
  },
  folder_iteration_out: {
    icon: '📤',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    label: 'Output',
  },
  folder_system: {
    icon: '⚙️',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    label: 'System',
  },
  folder_knowledge_root: {
    icon: '📚',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    label: 'Knowledge',
  },
  folder_knowledge: {
    icon: '📖',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    label: 'KB Entry',
  },

  // Documents
  'application/pdf': {
    icon: '📄',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    label: 'PDF',
  },
  'application/msword': {
    icon: '📝',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    label: 'Word',
  },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    icon: '📝',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    label: 'Word',
  },
  'application/vnd.ms-excel': {
    icon: '📊',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'Excel',
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    icon: '📊',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'Excel',
  },
  'application/vnd.ms-powerpoint': {
    icon: '📽️',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    label: 'PowerPoint',
  },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    icon: '📽️',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    label: 'PowerPoint',
  },

  // Text
  'text/plain': {
    icon: '📃',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    label: 'Text',
  },
  'text/markdown': {
    icon: '📑',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    label: 'Markdown',
  },
  'text/csv': {
    icon: '📋',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'CSV',
  },
  'text/html': {
    icon: '🌐',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    label: 'HTML',
  },
  'text/css': {
    icon: '🎨',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    label: 'CSS',
  },

  // Code
  'application/json': {
    icon: '{ }',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    label: 'JSON',
  },
  'application/javascript': {
    icon: '⚡',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
    label: 'JavaScript',
  },
  'text/javascript': {
    icon: '⚡',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
    label: 'JavaScript',
  },
  'application/typescript': {
    icon: '🔷',
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    label: 'TypeScript',
  },
  'text/x-python': {
    icon: '🐍',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'Python',
  },

  // Images
  'image/jpeg': {
    icon: '🖼️',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    label: 'JPEG',
  },
  'image/png': {
    icon: '🖼️',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    label: 'PNG',
  },
  'image/gif': {
    icon: '🎞️',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    label: 'GIF',
  },
  'image/svg+xml': {
    icon: '🔷',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    label: 'SVG',
  },
  'image/webp': {
    icon: '🖼️',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    label: 'WebP',
  },

  // Audio
  'audio/mpeg': {
    icon: '🎵',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    label: 'MP3',
  },
  'audio/wav': {
    icon: '🎵',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    label: 'WAV',
  },
  'audio/ogg': {
    icon: '🎵',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    label: 'OGG',
  },

  // Video
  'video/mp4': {
    icon: '🎬',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    label: 'MP4',
  },
  'video/webm': {
    icon: '🎬',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    label: 'WebM',
  },

  // Archives
  'application/zip': {
    icon: '📦',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    label: 'ZIP',
  },
  'application/x-rar-compressed': {
    icon: '📦',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    label: 'RAR',
  },
  'application/gzip': {
    icon: '📦',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    label: 'GZIP',
  },

  // Default
  default: {
    icon: '📄',
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    label: 'File',
  },
};

// =============================================================================
// Request Types
// =============================================================================

export interface CreateFolderRequest {
  name: string;
  parent_id?: string | null;
  description?: string | null;
  tags?: string[];
}

export interface UploadFileRequest {
  name: string;
  parent_id?: string | null;
  description?: string | null;
  tags?: string[];
}

export interface UpdateFileRequest {
  name?: string;
  description?: string | null;
  tags?: string[];
}

export interface MoveFileRequest {
  new_parent_id: string | null;
}

export interface CopyFileRequest {
  dest_parent_id: string | null;
  new_name?: string;
}

export interface BulkMoveRequest {
  file_ids: string[];
  dest_parent_id: string | null;
}

export interface BulkCopyRequest {
  file_ids: string[];
  dest_parent_id: string | null;
}

export interface BulkDeleteRequest {
  file_ids: string[];
}

export interface FilesFilter {
  query?: string;
  file_types?: string[];
  tags?: string[];
  folders_only?: boolean;
  files_only?: boolean;
  limit?: number;
}

// =============================================================================
// Response Types
// =============================================================================

export interface DownloadUrlResponse {
  url: string;
  file_name: string;
  expires_in: number;
}

export interface SearchResponse {
  files: FileNode[];
  total: number;
}

export interface FileStatsResponse {
  total_files: number;
  total_folders: number;
  total_size: number;
  by_type: Record<string, number>;
}

export interface UploadResponse {
  file: FileNode;
  message: string;
}

// =============================================================================
// Drag and Drop Types
// =============================================================================

export interface DragItem {
  type: 'file' | 'folder';
  id: string;
  name: string;
  parent_id: string | null;
}

export interface DropResult {
  target_id: string | null;
  action: 'move' | 'copy';
}

// =============================================================================
// Selection Types
// =============================================================================

export interface FilesSelection {
  selectedIds: Set<string>;
  lastSelectedId: string | null;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get file type configuration based on content type or folder type.
 */
export function getFileTypeConfig(node: FileNode): FileTypeConfig {
  if (node.is_folder) {
    // Check for special folder types
    if (node.folder_type && node.folder_type !== 'standard') {
      const folderTypeKey = `folder_${node.folder_type}`;
      if (FILE_TYPE_CONFIG[folderTypeKey]) {
        return FILE_TYPE_CONFIG[folderTypeKey];
      }
    }
    return FILE_TYPE_CONFIG.folder;
  }

  const contentType = node.content_type || '';

  // Try exact match first
  if (FILE_TYPE_CONFIG[contentType]) {
    return FILE_TYPE_CONFIG[contentType];
  }

  // Try matching by prefix (e.g., "text/*")
  const prefix = contentType.split('/')[0];
  for (const [key, config] of Object.entries(FILE_TYPE_CONFIG)) {
    if (key.startsWith(prefix + '/')) {
      return config;
    }
  }

  return FILE_TYPE_CONFIG.default;
}

/**
 * Format file size in human-readable format.
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  return `${size.toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`;
}

/**
 * Format date for display.
 */
export function formatFileDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  // Less than 24 hours - show time
  if (diff < 24 * 60 * 60 * 1000) {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Less than 7 days - show day name
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Same year - show date without year
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }

  // Different year - show full date
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get file extension from name.
 */
export function getFileExtension(name: string): string {
  const parts = name.split('.');
  if (parts.length < 2) return '';
  return parts[parts.length - 1].toLowerCase();
}

/**
 * Check if file is an image.
 */
export function isImageFile(node: FileNode): boolean {
  return node.content_type?.startsWith('image/') || false;
}

/**
 * Check if file is previewable text.
 */
export function isTextFile(node: FileNode): boolean {
  if (!node.content_type) return false;
  if (node.content_type.startsWith('text/')) return true;
  const textTypes = [
    'application/json',
    'application/javascript',
    'application/typescript',
    'application/xml',
    'application/x-yaml',
  ];
  return textTypes.includes(node.content_type);
}

/**
 * Check if file is a PDF.
 */
export function isPdfFile(node: FileNode): boolean {
  return node.content_type === 'application/pdf';
}

/**
 * Check if file is audio.
 */
export function isAudioFile(node: FileNode): boolean {
  return node.content_type?.startsWith('audio/') || false;
}

/**
 * Check if file is video.
 */
export function isVideoFile(node: FileNode): boolean {
  return node.content_type?.startsWith('video/') || false;
}

/**
 * Sort files with folders first, then by name.
 */
export function sortFiles(
  files: FileNode[],
  sortBy: FilesSortBy = 'name',
  sortOrder: FilesSortOrder = 'asc'
): FileNode[] {
  return [...files].sort((a, b) => {
    // Folders always come first
    if (a.is_folder && !b.is_folder) return -1;
    if (!a.is_folder && b.is_folder) return 1;

    let comparison = 0;

    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name, undefined, { numeric: true });
        break;
      case 'size':
        comparison = (a.file_size || 0) - (b.file_size || 0);
        break;
      case 'type':
        comparison = (a.content_type || '').localeCompare(b.content_type || '');
        break;
      case 'modified':
        comparison = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
        break;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });
}

/**
 * Build breadcrumb path from tree.
 */
export function buildBreadcrumbs(tree: TreeNode[], targetId: string | null): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [{ id: null, name: 'Files' }];

  if (!targetId) return breadcrumbs;

  function findPath(nodes: TreeNode[], target: string, path: BreadcrumbItem[]): boolean {
    for (const node of nodes) {
      const newPath = [...path, { id: node.node.id, name: node.node.name }];

      if (node.node.id === target) {
        breadcrumbs.push(...newPath);
        return true;
      }

      if (node.children.length > 0 && findPath(node.children, target, newPath)) {
        return true;
      }
    }
    return false;
  }

  findPath(tree, targetId, []);
  return breadcrumbs;
}

/**
 * Get MIME type from file extension.
 */
export function getMimeTypeFromExtension(filename: string): string {
  const ext = getFileExtension(filename);
  const mimeTypes: Record<string, string> = {
    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

    // Text
    'txt': 'text/plain',
    'md': 'text/markdown',
    'csv': 'text/csv',
    'html': 'text/html',
    'css': 'text/css',

    // Code
    'json': 'application/json',
    'js': 'application/javascript',
    'ts': 'application/typescript',
    'py': 'text/x-python',
    'xml': 'application/xml',
    'yaml': 'application/x-yaml',
    'yml': 'application/x-yaml',

    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'svg': 'image/svg+xml',
    'webp': 'image/webp',

    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',

    // Video
    'mp4': 'video/mp4',
    'webm': 'video/webm',

    // Archives
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    'gz': 'application/gzip',
  };

  return mimeTypes[ext] || 'application/octet-stream';
}
